import { Box } from '@mui/material';
import React, { useState } from 'react';
import { completeMultiPartUpload, initiateMultiPartUpload, MultiPartUpload, multiPartUploadUrl, Part } from '../Api';
import MobileUpload from '../components/MobileUpload';
import PWAInstall from '../components/PWAInstall';
import { useLocalStorage } from '../hooks/UseLocalStorage';
import { pwaManager } from '../utils/pwa';

const CHUNK_SIZE = 10000000;

const Upload: React.FC = () => {
    const [callLocal] = useLocalStorage("callLocal", false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState('');

    const handleFileSelect = async (file: File) => {
        setUploadStatus(`Selected: ${file.name} (${formatFileSize(file.size)})`);
        
        // Start upload automatically for better mobile UX
        await startUpload(file);
    };

    const preparePartsForFile = (file: File): Part[] => {
        const partSize = CHUNK_SIZE;
        let index = 0;
        let partNumber = 1;
        const parts = [];
        while (index < file.size) {
            parts.push({PartNumber: partNumber});
            partNumber += 1;
            index = index + partSize;
        }
        return parts;
    };

    const doPartUpload = async (part: Part, theMultiPartUpload: MultiPartUpload, file: File, partIndex: number, totalParts: number): Promise<void> => {
        const signedUploadUrl = await multiPartUploadUrl(callLocal, part.PartNumber, theMultiPartUpload);

        const fileStart = (part.PartNumber - 1) * CHUNK_SIZE;
        const fileSlice = file.slice(fileStart, fileStart + CHUNK_SIZE);

        // Create a custom fetch with progress tracking
        const xhr = new XMLHttpRequest();
        
        return new Promise((resolve, reject) => {
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    // Calculate overall progress: completed parts + current part progress
                    const completedPartsProgress = (partIndex / totalParts) * 100;
                    const currentPartProgress = (event.loaded / event.total) * (100 / totalParts);
                    const totalProgress = completedPartsProgress + currentPartProgress;
                    
                    setUploadProgress(Math.min(totalProgress, 100));
                    setUploadStatus(`Uploading ${file.name}... Part ${partIndex + 1}/${totalParts} (${Math.round(totalProgress)}%)`);
                }
            });

            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    const etag = xhr.getResponseHeader('ETag');
                    if (etag) {
                        part.ETag = etag;
                        resolve();
                    } else {
                        reject(new Error('No ETag received'));
                    }
                } else {
                    reject(new Error(`Upload failed with status ${xhr.status}`));
                }
            });

            xhr.addEventListener('error', () => {
                reject(new Error('Upload failed'));
            });

            xhr.open('PUT', signedUploadUrl);
            xhr.send(fileSlice);
        });
    };

    const startUpload = async (file: File) => {
        if (!file) return;

        setIsUploading(true);
        setUploadProgress(0);
        setUploadStatus('Requesting upload session from server...');

        try {
            // Initiate multipart upload
            const multiPartUpload = await initiateMultiPartUpload(callLocal, file);
            setUploadStatus('Upload session created, starting upload...');
            
            // Prepare parts
            const parts = preparePartsForFile(file);
            
            // Upload parts sequentially for better progress tracking
            for (let i = 0; i < parts.length; i++) {
                const part = parts[i];
                await doPartUpload(part, multiPartUpload, file, i, parts.length);
                
                // Update progress after each part completes
                const completedProgress = ((i + 1) / parts.length) * 100;
                setUploadProgress(completedProgress);
                setUploadStatus(`Uploading ${file.name}... Part ${i + 1}/${parts.length} completed (${Math.round(completedProgress)}%)`);
            }
            
            // Complete the upload
            setUploadStatus('Finalizing upload...');
            await completeMultiPartUpload(callLocal, multiPartUpload, parts);
            setUploadStatus('Upload completed successfully!');
            setUploadProgress(100);
            
            // Show notification if supported
            if (pwaManager.isRunningStandalone()) {
                pwaManager.showNotification('Upload Complete', {
                    body: `${file.name} has been uploaded successfully`,
                    tag: 'upload-success'
                });
            }

        } catch (error) {
            setUploadStatus('Upload failed. Please try again.');
            console.error('Upload error:', error);
        } finally {
            setIsUploading(false);
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <Box>
            <PWAInstall />
            <MobileUpload
                onFileSelect={handleFileSelect}
                uploadProgress={uploadProgress}
                isUploading={isUploading}
                uploadStatus={uploadStatus}
            />
        </Box>
    );
};

export default Upload;
